import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  createExerciseMode,
  deleteExerciseMode,
  listExerciseModes,
} from "@/api/catalog";
import type { LocationKind } from "@/api/types";
import { usePermission } from "@/auth/usePermission";

const LOCATIONS: LocationKind[] = ["ONSITE", "REMOTE", "MOBILE", "MIXED"];

export function ExerciseModesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canWrite = usePermission("exercise_mode:write");

  const { data, isLoading } = useQuery({
    queryKey: ["exercise-modes"],
    queryFn: () => listExerciseModes(),
  });

  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#1976d2");
  const [locationKind, setLocationKind] = useState<LocationKind>("ONSITE");
  const [baseWeight, setBaseWeight] = useState("1.0");
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createExerciseMode({
        code,
        label,
        color,
        location_kind: locationKind,
        base_weight: parseFloat(baseWeight) || 1.0,
      }),
    onSuccess: () => {
      setShowForm(false);
      setCode("");
      setLabel("");
      setColor("#1976d2");
      setLocationKind("ONSITE");
      setBaseWeight("1.0");
      setServerError(null);
      qc.invalidateQueries({ queryKey: ["exercise-modes"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteExerciseMode(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exercise-modes"] }),
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Erreur de suppression");
    },
  });

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      {canWrite && !showForm && (
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setShowForm(true)} sx={{ mb: 2 }}>
          {t("settings.addExerciseMode")}
        </Button>
      )}
      {showForm && (
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
          {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label={t("settings.code")} value={code} onChange={(e) => setCode(e.target.value)} size="small" required />
              <TextField label={t("settings.label")} value={label} onChange={(e) => setLabel(e.target.value)} size="small" required sx={{ flex: 1 }} />
              <TextField label={t("settings.color")} value={color} onChange={(e) => setColor(e.target.value)} size="small" type="color" sx={{ width: 100 }} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t("settings.locationKind")}</InputLabel>
                <Select
                  label={t("settings.locationKind")}
                  value={locationKind}
                  onChange={(e) => setLocationKind(e.target.value as LocationKind)}
                >
                  {LOCATIONS.map((k) => (
                    <MenuItem key={k} value={k}>{k}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t("settings.baseWeight")}
                value={baseWeight}
                onChange={(e) => setBaseWeight(e.target.value)}
                size="small"
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                sx={{ width: 160 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
              <Button variant="contained" onClick={() => createMutation.mutate()} disabled={!code || !label || createMutation.isPending}>
                {t("common.save")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <List>
        {(data ?? []).map((m) => (
          <ListItem
            key={m.id}
            divider
            secondaryAction={
              canWrite && !m.is_system && (
                <Tooltip title={t("settings.deleteConfirm")}>
                  <IconButton edge="end" color="error" onClick={() => {
                    if (confirm(t("settings.deleteConfirm"))) deleteMutation.mutate(m.id);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            }
          >
            <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: m.color ?? "grey.400", mr: 2 }} />
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{m.code}</Typography>
                  <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                  {m.is_system && <Chip size="small" label={t("settings.system")} color="secondary" variant="outlined" />}
                </Stack>
              }
              secondary={
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                  <Chip size="small" variant="outlined" label={m.location_kind} />
                  <Chip size="small" variant="outlined" label={`× ${m.base_weight}`} />
                  {!m.counts_as_worked && <Chip size="small" color="warning" variant="outlined" label="astreinte" />}
                  <Chip size="small" label={m.is_active ? "ACTIVE" : "INACTIVE"} color={m.is_active ? "success" : "default"} variant="outlined" />
                </Stack>
              }
              slotProps={{ secondary: { component: "div" } }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
